import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { X } from 'lucide-react';

interface ToastProps {
  title: string;
  description?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  type?: 'success' | 'error' | 'info';
}

// Add a ToastContext to manage toasts
interface ToastContextProps {
  showToast: (props: ToastProps) => void;
}

const ToastContext = React.createContext<ToastContextProps | undefined>(undefined);

export const Toast: React.FC<ToastProps & { onClose: () => void }> = ({ 
  title, 
  description, 
  action, 
  onClose,
  type = 'success'
}) => {
  return (
    <div 
      className={`fixed z-50 w-full max-w-sm overflow-hidden rounded-lg shadow-lg 
        ${type === 'success' ? 'bg-green-100 border border-green-200' :
          type === 'error' ? 'bg-red-100 border border-red-200' :
          'bg-white border border-gray-200'}`}
      style={{
        // Push below the iOS safe area (notch / status bar) instead of under it.
        top: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        right: 'calc(env(safe-area-inset-right, 0px) + 1rem)',
      }}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-1 mr-3">
            <h3 className={`text-sm font-medium 
              ${type === 'success' ? 'text-green-900' :
                type === 'error' ? 'text-red-900' :
                'text-gray-900'}`}>
              {title}
            </h3>
            {description && (
              <p className={`mt-1 text-sm 
                ${type === 'success' ? 'text-green-700' :
                  type === 'error' ? 'text-red-700' :
                  'text-gray-700'}`}>
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            className={`inline-flex shrink-0 rounded-md p-1.5 
              ${type === 'success' ? 'text-green-700 hover:bg-green-200' :
                type === 'error' ? 'text-red-700 hover:bg-red-200' :
                'text-gray-600 hover:bg-gray-100'}`}
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {action && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => {
                action.onClick();
                onClose();
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium 
                ${type === 'success' ? 'bg-green-600 text-white hover:bg-green-700' :
                  type === 'error' ? 'bg-red-600 text-white hover:bg-red-700' :
                  'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              {action.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

  const showToast = (toast: ToastProps) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { ...toast, id }]);
    
    // Auto dismiss after duration
    if (toast.duration) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, toast.duration);
    }
  };

  // Create portal container
  useEffect(() => {
    // Create container if it doesn't exist
    if (!document.getElementById('toast-container')) {
      const container = document.createElement('div');
      container.id = 'toast-container';
      document.body.appendChild(container);
    }

    // Cleanup on unmount
    return () => {
      const container = document.getElementById('toast-container');
      if (container && container.childNodes.length === 0) {
        document.body.removeChild(container);
      }
    };
  }, []);

  // Render toasts
  useEffect(() => {
    const container = document.getElementById('toast-container');
    if (!container) return;

    // Clear container
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Add each toast
    toasts.forEach((toast) => {
      const toastDiv = document.createElement('div');
      toastDiv.id = `toast-${toast.id}`;
      container.appendChild(toastDiv);

      const root = createRoot(toastDiv);
      root.render(
        <Toast
          {...toast}
          onClose={() => {
            setToasts(prev => prev.filter(t => t.id !== toast.id));
          }}
        />
      );
    });
  }, [toasts]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Helper functions for quickly showing different types of toasts
export const toast = {
  success: (props: Omit<ToastProps, 'type'>) => {
    const container = document.getElementById('toast-container');
    if (!container) {
      console.error('Toast container not found');
      return;
    }

    const toastDiv = document.createElement('div');
    const id = Math.random().toString(36).substring(2, 9);
    toastDiv.id = `toast-${id}`;
    container.appendChild(toastDiv);

    const root = createRoot(toastDiv);
    root.render(
      <Toast
        {...props}
        type="success"
        onClose={() => {
          const element = document.getElementById(`toast-${id}`);
          if (element) element.remove();
        }}
      />
    );

    if (props.duration) {
      setTimeout(() => {
        const element = document.getElementById(`toast-${id}`);
        if (element) element.remove();
      }, props.duration);
    }
  },
  
  error: (props: Omit<ToastProps, 'type'>) => {
    const container = document.getElementById('toast-container');
    if (!container) {
      console.error('Toast container not found');
      return;
    }

    const toastDiv = document.createElement('div');
    const id = Math.random().toString(36).substring(2, 9);
    toastDiv.id = `toast-${id}`;
    container.appendChild(toastDiv);

    const root = createRoot(toastDiv);
    root.render(
      <Toast
        {...props}
        type="error"
        onClose={() => {
          const element = document.getElementById(`toast-${id}`);
          if (element) element.remove();
        }}
      />
    );

    if (props.duration) {
      setTimeout(() => {
        const element = document.getElementById(`toast-${id}`);
        if (element) element.remove();
      }, props.duration);
    }
  },
  
  info: (props: Omit<ToastProps, 'type'>) => {
    const container = document.getElementById('toast-container');
    if (!container) {
      console.error('Toast container not found');
      return;
    }

    const toastDiv = document.createElement('div');
    const id = Math.random().toString(36).substring(2, 9);
    toastDiv.id = `toast-${id}`;
    container.appendChild(toastDiv);

    const root = createRoot(toastDiv);
    root.render(
      <Toast
        {...props}
        type="info"
        onClose={() => {
          const element = document.getElementById(`toast-${id}`);
          if (element) element.remove();
        }}
      />
    );

    if (props.duration) {
      setTimeout(() => {
        const element = document.getElementById(`toast-${id}`);
        if (element) element.remove();
      }, props.duration);
    }
  }
}; 